import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { VyLoader } from '../../../../../../../../components/new-design/feedback/Loader/VyLoader';
import { extract, formatNumber, isNullOrUndefined, vyDate } from '@vy/utils';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';
import { UsageByDateBreakdown } from '@vy/types';
import { buildProductUsageChartData } from './product-usage-breakdown-chart-utils/build-product-usage-chart-data';
import { ProductUsageBreakdownDateResolution } from './product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import { useMemo } from 'react';
import { PRODUCT_USAGE_CHART_DATA_KEY } from './product-usage-breakdown-chart-types/product-usage-chart-data-key';
import { ProductUsageBreakdownChartTooltip } from './ProductUsageBreakdownChartTooltip';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { Box, GlobalStyles } from '@mui/material';
import { getChartSeriesWithLabels } from './product-usage-breakdown-chart-utils/build-chart-series-with-labels';
import { ChartsOverlay } from '@mui/x-charts/ChartsOverlay';
import { ProductUsageBreakdownChartOverageOverlay } from './ProductUsageBreakdownChartOverageOverlay';
import { max, min } from 'lodash';
import {
  ProductUsageBreakdownChartBar,
  ProductUsageBreakdownChartBarProps,
} from './ProductUsageBreakdownChartBar';
import { ProductUsageBreakdownChartLegend } from './ProductUsageBreakdownChartLegend';

type ProductUsageBreakdownChartProps = {
  isLoading: boolean;
  usageByDateBreakdowns: UsageByDateBreakdown[];
  overageCrossedDate?: Date;
  dateResolution: ProductUsageBreakdownDateResolution;
};

const CHART_GRID_STYLE_ID = 'product-usage-custom-grid-line';

export const ProductUsageBreakdownChart: React.FC<ProductUsageBreakdownChartProps> = ({
  isLoading,
  usageByDateBreakdowns,
  overageCrossedDate,
  dateResolution,
}) => {
  if (isLoading) {
    return <VyLoader height={400} />;
  }

  const chartData = useMemo(
    () => buildProductUsageChartData(usageByDateBreakdowns, dateResolution),
    [usageByDateBreakdowns, dateResolution],
  );

  const chartSeries = useMemo(() => getChartSeriesWithLabels(chartData, usageByDateBreakdowns), [chartData]);

  const { topSeriesId, bottomSeriesId } = useMemo(
    () => ({
      bottomSeriesId: chartSeries[0]?.id,
      topSeriesId: chartSeries[chartSeries.length - 1]?.id,
    }),
    [chartSeries],
  );

  const overageRatio = calculateOverageRatio(usageByDateBreakdowns, overageCrossedDate);

  return (
    <Box>
    <ChartsContainer
      dataset={chartData}
      xAxis={[
        {
          scaleType: 'band',
          dataKey: PRODUCT_USAGE_CHART_DATA_KEY,
          tickLabelStyle: {
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fill: '#5F6978',
          },
        },
      ]}
      yAxis={[
        {
          tickLabelStyle: {
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fill: '#5F6978',
          },
          valueFormatter: (value: number) => (value === 0 ? '0' : formatNumber(value)),
        },
      ]}
      series={chartSeries}
      height={400}
      margin={{ left: 70, right: 20, top: 20, bottom: 20 }}
      sx={{
        '& .MuiChartsBar-root': {
          transition: 'fill 0.2s ease-in-out',
        },
      }}
    >
      <ChartsGrid horizontal classes={{ line: CHART_GRID_STYLE_ID }} />
      <ChartsOverlay
        loading={overageRatio > 0}
        slots={{
          loadingOverlay: () => (
            <ProductUsageBreakdownChartOverageOverlay
              overageCrossedDate={overageCrossedDate}
              xAxisOverlayRatio={overageRatio}
            />
          ),
        }}
      />
      <BarPlot
        slots={{
          bar: (props) => (
            <ProductUsageBreakdownChartBar
              {...(props as ProductUsageBreakdownChartBarProps)}
              topSeriesId={topSeriesId}
              bottomSeriesId={bottomSeriesId}
            />
          ),
        }}
      />
      <ChartsXAxis disableLine disableTicks />
      <ChartsYAxis disableLine disableTicks />
      <GlobalStyles
        styles={{
          [`.${CHART_GRID_STYLE_ID}`]: {
            stroke: '#E8EBF2 !important',
            strokeWidth: '1 !important',
          },
        }}
      />
      <ChartsTooltipContainer trigger="axis">
        <ProductUsageBreakdownChartTooltip />
      </ChartsTooltipContainer>
    </ChartsContainer>
      <ProductUsageBreakdownChartLegend series={chartSeries} />
    </Box>
  );
};

function calculateOverageRatio(usageByDateBreakdowns: UsageByDateBreakdown[], overageCrossedDate?: Date) {
  if (isNullOrUndefined(overageCrossedDate)) {
    return 0;
  }

  const usageDates = extract(usageByDateBreakdowns, 'date');

  const maxUsageDate = max(usageDates);
  const minUsageDate = min(usageDates);

  const fullUsageDays = vyDate(maxUsageDate).diff(vyDate(minUsageDate), 'days', true);
  const overageDays = vyDate(overageCrossedDate).diff(vyDate(minUsageDate), 'days', true);

  return overageDays / fullUsageDays;
}
